import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { 
  FaRunning, 
  FaDumbbell, 
  FaHeart
} from 'react-icons/fa';
import { 
  GiMuscleUp,
  GiLungs,
  GiStopSign
} from 'react-icons/gi';
import { PersonalizationBadgeGroup } from './common/PersonalizationIndicator';
import './ExerciseGuide.css';
import './SmartDailySummary.css';

function ExerciseGuide({ exerciseInfo, mode, currentWeek, trimester, profile }) {
  const { user } = useAuth();
  const userId = user?.userId || user?.id;
  const [activeTab, setActiveTab] = useState('safe');
  const [expandedExercises, setExpandedExercises] = useState(new Set());
  const [completedExercises, setCompletedExercises] = useState(new Set());
  const [userPreferences, setUserPreferences] = useState(null);
  
  const loadUserPreferences = useCallback(async () => {
    try {
      const prefsData = await api.getUserPreferences(userId);
      if (prefsData) {
        setUserPreferences(prefsData);
      }
    } catch (error) {
      console.warn('Could not load user preferences:', error);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadUserPreferences();
    }
  }, [userId, loadUserPreferences]);
  
  if (!exerciseInfo) {
    return null;
  }
  
  const getActivityLevelLabel = (level) => {
    const labels = {
      'SEDENTARY': 'Light Activity',
      'MODERATE': 'Moderate Activity',
      'ACTIVE': 'Active Lifestyle'
    };
    return labels[level] || level;
  };

  const safeWorkouts = exerciseInfo.safeWorkouts || [];
  const contraindicated = exerciseInfo.contraindicatedWorkouts || [];
  const pelvicFloor = exerciseInfo.pelvicFloorExercises || [];
  const breathing = exerciseInfo.breathingPractices || [];

  const toggleExercise = (exerciseId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Toggle the specific exercise independently - allow multiple to be expanded
    setExpandedExercises(prev => {
      const newSet = new Set(prev);
      if (newSet.has(exerciseId)) {
        newSet.delete(exerciseId);
      } else {
        newSet.add(exerciseId);
      }
      return newSet;
    });
  };

  const markCompleted = (exerciseId) => {
    setCompletedExercises(prev => {
      const newSet = new Set(prev);
      if (newSet.has(exerciseId)) {
        newSet.delete(exerciseId);
      } else {
        newSet.add(exerciseId);
      }
      return newSet;
    });
  };

  const tabs = [
    { id: 'safe', label: 'Safe Exercises', Icon: FaRunning, count: safeWorkouts.length, color: '#4CAF50' },
    { id: 'pelvic', label: 'Pelvic Floor', Icon: GiMuscleUp, count: pelvicFloor.length, color: '#E91E63' },
    { id: 'breathing', label: 'Breathing', Icon: GiLungs, count: breathing.length, color: '#2196F3' },
    { id: 'avoid', label: 'To Avoid', Icon: GiStopSign, count: contraindicated.length, color: '#FF9800' }
  ];

  const getMotivationalMessage = () => {
    const messages = [
      "You're doing amazing! Every movement counts. 💪",
      "Your body is incredible - honor it with gentle movement. 🌸",
      "Small steps lead to big wins. You've got this! ✨",
      "Listen to your body - it knows what you need. 💕",
      "You're growing a human - that's the ultimate workout! 👶",
      "Rest is just as important as movement. Be kind to yourself. 🕯️"
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  const getIntensityColor = (intensity) => {
    if (!intensity) return '#9E9E9E';
    const lower = intensity.toLowerCase();
    if (lower.includes('light') || lower.includes('gentle')) return '#4CAF50';
    if (lower.includes('moderate')) return '#FFC107';
    if (lower.includes('high') || lower.includes('intense')) return '#F44336';
    return '#2196F3';
  };

  const renderExerciseCard = (exercise, index, type) => {
    const exerciseId = `${type}-${index}`;
    const isExpanded = expandedExercises.has(exerciseId);
    const isCompleted = completedExercises.has(exerciseId);
    
    return (
      <motion.div
        key={exerciseId}
        className={`exercise-card-modern ${isCompleted ? 'completed' : ''} ${isExpanded ? 'expanded' : ''}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
        whileHover={{ scale: 1.02, y: -4 }}
      >
        <div className="exercise-card-header" onClick={(e) => toggleExercise(exerciseId, e)}>
          <div className="exercise-card-main">
            <div className="exercise-icon-wrapper">
              <motion.div
                className="exercise-icon"
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {type === 'safe' && '🏃'}
                {type === 'pelvic' && '💪'}
                {type === 'breathing' && '🧘'}
                {type === 'avoid' && '⚠️'}
              </motion.div>
              {isCompleted && (
                <motion.div
                  className="completed-badge"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                >
                  ✓
                </motion.div>
              )}
            </div>
            <div className="exercise-card-content">
              <h4 className="exercise-name">{exercise.name}</h4>
              <p className="exercise-description-preview">{exercise.description}</p>
              <div className="exercise-meta">
                {exercise.duration && (
                  <span className="meta-badge time">
                    ⏱️ {exercise.duration}
                  </span>
                )}
                {exercise.intensity && (
                  <span 
                    className="meta-badge intensity"
                    style={{ backgroundColor: getIntensityColor(exercise.intensity) + '20', color: getIntensityColor(exercise.intensity) }}
                  >
                    {exercise.intensity}
                  </span>
                )}
                {exercise.frequency && (
                  <span className="meta-badge frequency">
                    📅 {exercise.frequency}
                  </span>
                )}
              </div>
            </div>
          </div>
          <motion.button
            className="expand-button"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggleExercise(exerciseId, e);
            }}
          >
            {isExpanded ? '▲' : '▼'}
          </motion.button>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              className="exercise-card-details"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {exercise.howTo && (
                <div className="detail-section how-to-section">
                  <div className="section-header">
                    <span className="section-icon">📝</span>
                    <h5>How to Do It</h5>
                  </div>
                  <div className="how-to-steps">
                    {exercise.howTo.split(/[.\n]/).filter(step => step.trim()).map((step, idx) => (
                      <div key={idx} className="step-item">
                        <span className="step-number">{idx + 1}</span>
                        <span className="step-text">{step.trim()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {exercise.benefits && exercise.benefits.length > 0 && (
                <div className="detail-section benefits-section">
                  <div className="section-header">
                    <span className="section-icon">✨</span>
                    <h5>Benefits for You & Baby</h5>
                  </div>
                  <div className="benefits-grid">
                    {exercise.benefits.map((benefit, idx) => (
                      <motion.div
                        key={idx}
                        className="benefit-item"
                        whileHover={{ scale: 1.05 }}
                      >
                        <span className="benefit-icon">💚</span>
                        <span className="benefit-text">{benefit}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {exercise.precautions && (
                <div className="detail-section precautions-section">
                  <div className="section-header">
                    <span className="section-icon">⚠️</span>
                    <h5>Important Safety Notes</h5>
                  </div>
                  <p className="precautions-text">{exercise.precautions}</p>
                </div>
              )}

              {type !== 'avoid' && (
                <div className="exercise-actions">
                  <motion.button
                    className={`action-button complete-button ${isCompleted ? 'completed' : ''}`}
                    onClick={() => markCompleted(exerciseId)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {isCompleted ? '✓ Completed Today' : '✓ Mark as Done'}
                  </motion.button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  const renderContraindicatedCard = (workout, index) => {
    return (
      <motion.div
        key={`avoid-${index}`}
        className="avoid-card-modern"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
        whileHover={{ scale: 1.02, y: -4 }}
      >
        <div className="avoid-header">
          <div className="avoid-icon-wrapper">
            <GiStopSign className="avoid-icon" />
          </div>
          <h4 className="avoid-card-title">{workout.name}</h4>
        </div>
        <div className="avoid-reason">
          <strong>Why to avoid:</strong>
          <p>{workout.reason}</p>
        </div>
        {workout.examples && workout.examples.length > 0 && (
          <div className="avoid-examples">
            <strong>Examples:</strong>
            <div className="examples-tags">
              {workout.examples.map((example, idx) => (
                <span key={idx} className="example-tag">{example}</span>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="exercise-guide-modern">
      {/* Hero Section */}
      <motion.div
        className="exercise-hero"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="hero-content">
          <motion.div
            className="hero-icon"
            animate={{ 
              rotate: [0, -10, 10, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity,
              repeatDelay: 2
            }}
          >
            <FaDumbbell />
          </motion.div>
          <div className="hero-text">
            <h2>Your Movement Journey</h2>
            <p className="hero-subtitle">
              {mode === 'pregnant' && currentWeek && `Week ${currentWeek} • `}
              {mode === 'pregnant' && trimester && `Trimester ${trimester} • `}
              Safe, gentle exercises tailored for you
            </p>
            <motion.p
              className="motivational-message"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {getMotivationalMessage()}
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Personalization Indicators */}
      {userPreferences && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{ marginBottom: '24px' }}
        >
          <PersonalizationBadgeGroup
            factors={[
              (userPreferences.activityLevel || profile?.activityLevel) && {
                value: userPreferences.activityLevel || profile?.activityLevel,
                label: getActivityLevelLabel(userPreferences.activityLevel || profile?.activityLevel),
                impact: 'Exercise intensity matched to your lifestyle',
                icon: '🏃',
                color: '#28a745',
                compact: true
              },
              currentWeek && {
                value: currentWeek,
                label: `Week ${currentWeek} safe exercises`,
                impact: 'Exercises approved for your pregnancy stage',
                icon: '🤰',
                color: '#E91E63',
                compact: true
              },
              (userPreferences.complications || profile?.preexistingConditions) && {
                value: true,
                label: 'Health-adjusted',
                impact: 'Recommendations modified for your safety',
                icon: '🏥',
                color: '#ffc107',
                compact: true
              }
            ].filter(Boolean)}
            title="Exercise recommendations personalized for:"
          />
        </motion.div>
      )}

      {/* Tabs */}
      <div className="exercise-tabs-modern">
        {tabs.map((tab) => (
          <motion.button
            key={tab.id}
            className={`exercise-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            style={{
              '--tab-color': tab.color,
              borderColor: activeTab === tab.id ? tab.color : 'transparent'
            }}
          >
            <span className="tab-icon">
              <tab.Icon />
            </span>
            <span className="tab-label">{tab.label}</span>
            {tab.count > 0 && (
              <span className="tab-count">{tab.count}</span>
            )}
          </motion.button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          className="exercise-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'safe' && (
            <>
              {safeWorkouts.length > 0 ? (
                <div className="exercise-tab-content safe-exercises-content">
                  <div className="exercises-grid-modern">
                    {safeWorkouts.map((workout, idx) => renderExerciseCard(workout, idx, 'safe'))}
                  </div>
                </div>
              ) : (
                <div className="empty-exercise-state-modern">
                  <FaRunning className="empty-icon-modern" />
                  <h3 className="empty-title-modern">No exercises available</h3>
                  <p className="empty-text-modern">Exercise recommendations will be personalized based on your profile.</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'pelvic' && (
            <>
              {pelvicFloor.length > 0 ? (
                <div className="exercise-tab-content pelvic-floor-content">
                  <div className="exercises-grid-modern">
                    {pelvicFloor.map((exercise, idx) => renderExerciseCard(exercise, idx, 'pelvic'))}
                  </div>
                </div>
              ) : (
                <div className="empty-exercise-state-modern">
                  <GiMuscleUp className="empty-icon-modern" />
                  <h3 className="empty-title-modern">No pelvic floor exercises available</h3>
                  <p className="empty-text-modern">Check back soon for personalized pelvic floor exercises.</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'breathing' && (
            <>
              {breathing.length > 0 ? (
                <div className="exercise-tab-content breathing-content">
                  <div className="exercises-grid-modern">
                    {breathing.map((practice, idx) => renderExerciseCard(practice, idx, 'breathing'))}
                  </div>
                </div>
              ) : (
                <div className="empty-exercise-state-modern">
                  <GiLungs className="empty-icon-modern" />
                  <h3 className="empty-title-modern">No breathing practices available</h3>
                  <p className="empty-text-modern">Breathing exercises will be added based on your needs.</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'avoid' && (
            <>
              {contraindicated.length > 0 ? (
                <div className="exercise-tab-content avoid-content">
                  <div className="avoid-intro-modern">
                    <div className="avoid-intro-icon-wrapper">
                      <GiStopSign className="avoid-intro-icon" />
                    </div>
                    <div className="avoid-intro-text">
                      <h3 className="avoid-intro-title">Important Safety Notice</h3>
                      <p className="avoid-intro-description">These exercises should be avoided during your current stage. Your safety and your baby's wellbeing come first!</p>
                    </div>
                  </div>
                  <div className="avoid-grid-modern">
                    {contraindicated.map((workout, idx) => renderContraindicatedCard(workout, idx))}
                  </div>
                </div>
              ) : (
                <div className="empty-exercise-state-modern">
                  <GiStopSign className="empty-icon-modern" />
                  <h3 className="empty-title-modern">Great news!</h3>
                  <p className="empty-text-modern">No specific exercises to avoid at this time. Always listen to your body and consult your healthcare provider.</p>
                </div>
              )}
            </>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Stop Signs - Modern Redesign */}
      {exerciseInfo.stopSigns && exerciseInfo.stopSigns.length > 0 && (
        <motion.section
          className="summary-section stop-signs-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="section-header">
            <div className="section-header-content">
              <div className="section-icon-wrapper">
                <GiStopSign className="section-icon" />
              </div>
              <div>
                <h2>Stop Exercise If You Experience</h2>
                <p className="section-subtitle-creative">
                  Your safety is our top priority. Listen to your body and rest when needed.
                </p>
              </div>
            </div>
          </div>
          
          <div className="section-content">
            <div className="stop-signs-grid-modern">
              {exerciseInfo.stopSigns.map((sign, idx) => {
                // Map each stop sign to a brief description
                const stopSignDescriptions = {
                  'Vaginal bleeding': 'Any bleeding during pregnancy should be evaluated by your healthcare provider immediately.',
                  'Dizziness or feeling faint': 'This may indicate low blood pressure or dehydration. Stop and rest immediately.',
                  'Chest pain': 'Chest pain during exercise requires immediate medical attention. Do not continue exercising.',
                  'Shortness of breath before exercise': 'If you\'re short of breath before starting, rest and consult your doctor.',
                  'Headache': 'Severe or persistent headaches during exercise may indicate a problem. Stop and rest.',
                  'Muscle weakness': 'Sudden muscle weakness could indicate fatigue or other issues. Take a break.',
                  'Calf pain or swelling': 'Could indicate a blood clot (DVT). Stop exercising and contact your doctor immediately.',
                  'Contractions': 'Regular contractions may indicate preterm labor. Stop and contact your healthcare provider.',
                  'Decreased fetal movement': 'If you notice less movement, stop exercising and monitor. Contact your doctor if concerned.',
                  'Fluid leaking from vagina': 'This could indicate your water breaking. Stop exercising and contact your healthcare provider immediately.'
                };
                
                const description = stopSignDescriptions[sign] || 'If you experience this symptom, stop exercising and consult your healthcare provider.';
                
                return (
                  <motion.div
                    key={idx}
                    className="stop-sign-card-modern"
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: 0.5 + idx * 0.1, type: "spring", stiffness: 100 }}
                    whileHover={{ scale: 1.03, y: -5 }}
                  >
                    <div className="stop-sign-card-background"></div>
                    <div className="stop-sign-card-content-wrapper">
                      <div className="stop-sign-icon-wrapper-modern">
                        <GiStopSign className="stop-sign-icon-svg" />
                        <div className="icon-pulse-stop"></div>
                      </div>
                      <div className="stop-sign-info-modern">
                        <h3 className="stop-sign-title-modern">{sign}</h3>
                        <p className="stop-sign-description-modern">{description}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            
            <motion.div
              className="stop-signs-reminder-modern"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <div className="reminder-icon-wrapper-modern">
                <FaHeart className="reminder-icon-svg" />
                <div className="icon-glow-reminder"></div>
              </div>
              <div className="reminder-content-modern">
                <h4 className="reminder-title-modern">Remember</h4>
                <p className="reminder-text-modern">Your body is doing incredible work. Rest is not failure - it's essential for you and your baby's well-being.</p>
              </div>
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Progress Summary */}
      {completedExercises.size > 0 && (
        <motion.div
          className="progress-summary"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="progress-content">
            <span className="progress-icon">🎉</span>
            <div className="progress-text">
              <strong>You've completed {completedExercises.size} exercise{completedExercises.size !== 1 ? 's' : ''} today!</strong>
              <p>You're doing amazing! Keep up the great work! 💪✨</p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default ExerciseGuide;
